// If we can see and click on HelloWorld2Secure
// TODO:JJ - I wanted to use a more advanced library - like CASL - but it was too complex

import { UserClaims } from "../types/users";

// A Claim Create function takes a claim arg
// This is stored in the claims in the JWT
type ClaimCreate<T> = (claimArgs: T) => { [x: string]: T };

// A Claim Test function takes claim args and returns true/false
type ClaimCan<T> = (claims: UserClaims | undefined, args: T | undefined) => boolean;

// We can take the function input and build the claim args
export type ClaimArgBuilder<TArgs> = (data: any) => TArgs;

// A rule takes a user, and returns the Action and a Can function
export type SecurityRule<TClaim, TArgs> = {
  name: string;
  createClaim: ClaimCreate<TClaim>;
  can: ClaimCan<TArgs>;
  builder?: ClaimArgBuilder<TArgs>;
};

export type SecurityRuleFactory<TClaim, TArgs> = () => SecurityRule<TClaim, TArgs>;
export type SecurityRuleFactoryWithBuilder<TClaim, TArgs> = (builder: ClaimArgBuilder<TArgs>) => SecurityRule<TClaim, TArgs>;

export const getClaim = <T>(claims: UserClaims, claim: string): T | undefined => {
  if (hasClaim(claims, claim)) {
    return (claims as any)[claim] as T;
  } else {
    return undefined;
  }
};

export const hasClaim = (claims: UserClaims | undefined, claim: string): boolean => {
  return Boolean(claims?.hasOwnProperty(claim));
};

// A Claim Test function takes claim args and returns true/false
type ClaimCanInner<TClaim, TArgs> = (claims: UserClaims, callerEmail: string, claim: TClaim, args: TArgs | undefined) => boolean;

export const createRule = <TClaim, TArgs>(name: string, can: ClaimCanInner<TClaim, TArgs>, builder?: ClaimArgBuilder<TArgs>): SecurityRule<TClaim, TArgs> => {
  return {
    name: name,
    createClaim: (claimArgs: TClaim) => {
      return { [name]: claimArgs }; // This is stored in the claims in the JWT
    },
    can: (claims: UserClaims | undefined, args: TArgs | undefined): boolean => {
      if (claims) {
        // the user claims MUST contain a caller email - or its a problem
        const callerEmail: string | undefined = claims.email as string;
        if (!callerEmail) return false;

        const claim = getClaim<TClaim>(claims!, name);
        if (claim) {
          return can(claims!, callerEmail, claim, args);
        }
      }

      return false;
    },
    builder: builder,
  };
};

/**
 * Factory for simple boolean presence claim rules that pass whenever the user holds the named claim.
 *
 * @param {string} claimName - Canonical JWT claim key.
 * @returns {SecurityRuleFactory<TClaim, TArgs>} Security rule factory.
 */
export const createSimpleClaimRule = <TClaim = string | string[], TArgs = Record<string, never>>(
  claimName: string
): SecurityRuleFactory<TClaim, TArgs> => {
  const testCan = (): boolean => true;
  return (): SecurityRule<TClaim, TArgs> => createRule<TClaim, TArgs>(claimName, testCan);
};
